import { Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { validation } from "./FormModel/validationSchema";
import { useMutation, useQueryClient } from "react-query";
import { ProjectImage, ProjectProfile } from "./steps";
import { Button, Form, Modal, ModalBody, ModalHeader } from "react-bootstrap";
import {
  addProjectImage,
  addProjectMaterials,
  createProject,
  updateProject,
} from "../../services/projectCrud";
import { styles } from "@components/styles";
import * as Yup from "yup";
import ImageDropzone from "@components/ImageDropzone";
import { ImageAddIcon } from "@components/icons";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("validation.project_name_required")
    .max(100, "validation.project_name_max_length"),
});

const steps = ["projectname", "image"];

const renderSteps = (activeStep) => {
  switch (activeStep) {
    case 0:
      return <ProjectProfile validation={validation} />;
    case 1:
      return <>Not Found</>;
  }
};

function ProjectForm({
  show,
  isUpdate,
  setIsUpdate,
  setOpenForm,
  selectedProject,
  renderMode = "modal",
}) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [newId, setNewId] = useState();
  const isLastStep = activeStep === steps.length - 1;
  const isSecondLastStep = activeStep === steps.length - 2;
  const queryClient = useQueryClient();

  const handleClose = () => {
    setIsUpdate && setIsUpdate(false);
    setOpenForm(false);
  };

  const { isLoading, mutateAsync: projectMutate } = useMutation(
    isUpdate ? "updateProject" : "createproject",
    isUpdate ? updateProject : createProject,
    {
      onSuccess: (data) => {
        setNewId(data.id);
        !isLastStep && setActiveStep(activeStep + 1);
        queryClient.invalidateQueries("projects");
        if (isUpdate && selectedProject?.id) {
          queryClient.invalidateQueries(["project", String(selectedProject.id)]);
        }
      },
    }
  );

  const handleSubmit = (values) => {
    if (isSecondLastStep) {
      const payload = { ...values };
      delete payload.materialsFiles;
      isUpdate
        ? projectMutate({ formData: payload, id: selectedProject.id })
        : projectMutate(payload);
    }
  };

  const handleInlineSubmit = async (values, actions) => {
    const { setSubmitting } = actions;
    try {
      const payload = { ...values };
      delete payload.image;
      delete payload.materialsFiles;
      const response = isUpdate
        ? await projectMutate({ formData: payload, id: selectedProject.id })
        : await projectMutate(payload);
      const projectId = isUpdate ? selectedProject?.id : response?.id || newId;
      if (values.image && projectId) {
        const form = new FormData();
        form.append("image", values.image);
        await addProjectImage({ form, pId: projectId });
      }
      if (values.materialsFiles?.length && projectId) {
        const form = new FormData();
        values.materialsFiles.forEach((file) => {
          form.append("files", file);
        });
        await addProjectMaterials({ form, pId: projectId });
      }
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (renderMode === "inline") {
    return (
      <div style={{ height: "100%", overflowY: "auto", background: "#fff" }}>
        <div style={{ padding: "12px", borderBottom: "1px solid #e5e7eb" }}>
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">
              {isUpdate ? `${t("update")} ${selectedProject?.name || ""}` : t("newproject")}
            </h5>
            <Button size="sm" variant="outline-secondary" onClick={handleClose}>
              {t("cancel")}
            </Button>
          </div>
        </div>
        <div style={{ padding: "12px" }}>
          <Formik
            initialValues={
              isUpdate
                ? {
                    name: selectedProject.name || "",
                    aim: selectedProject.aim || "",
                    description: selectedProject.description || "",
                    research_question: selectedProject.research_question || "",
                    methodology: selectedProject.methodology || "",
                    findings: selectedProject.findings || "",
                    challenges: selectedProject.challenges || "",
                    materials: selectedProject.materials || "",
                    materialsFiles: [],
                    image: "",
                  }
                : {
                    name: "",
                    aim: "",
                    description: "",
                    research_question: "",
                    methodology: "",
                    findings: "",
                    challenges: "",
                    materials: "",
                    materialsFiles: [],
                    image: "",
                  }
            }
            validationSchema={validationSchema}
            onSubmit={handleInlineSubmit}
          >
            {({ values, errors, setFieldValue, handleSubmit, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <ProjectProfile validation={validation} />
                <div className="mt-3">
                  <label className="mb-2" htmlFor="">
                    {t("change")} {t("image")}
                  </label>
                  <ImageDropzone
                    img={isUpdate ? selectedProject?.image : <ImageAddIcon />}
                    setFieldValue={setFieldValue}
                  />
                </div>
                <div className="d-flex justify-content-end pt-3">
                  <Button type="submit" variant="outline-primary" disabled={isSubmitting}>
                    {isSubmitting ? t("button.saving") : t("button.save")}
                  </Button>
                </div>
                <span className="text-danger">{errors?.name && t(errors.name)}</span>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  }
  return (
    <Modal data-testid="project-modal" onHide={handleClose} show={show} centered>
      <ModalHeader closeButton>
        <h5 className="mb-0">
          {isUpdate ? `${t("update")} ${selectedProject.name}` : t("newproject")}
        </h5>
      </ModalHeader>
      <ModalBody>
        <div className={styles.wizardList}>
          {steps.map((step, i) => (
            <span key={step} className={activeStep == i ? "wizard-active" : ""}>
              {t(step)}
            </span>
          ))}
        </div>
        {!isLastStep ? (
          <Formik
            initialValues={
              isUpdate
                ? {
                    name: selectedProject.name || "",
                    aim: selectedProject.aim || "",
                    description: selectedProject.description || "",
                    research_question: selectedProject.research_question || "",
                    methodology: selectedProject.methodology || "",
                    findings: selectedProject.findings || "",
                    challenges: selectedProject.challenges || "",
                    materials: selectedProject.materials || "",
                    materialsFiles: [],
                  }
                : {
                    name: "",
                    aim: "",
                    description: "",
                    research_question: "",
                    methodology: "",
                    findings: "",
                    challenges: "",
                    materials: "",
                    materialsFiles: [],
                  }
            }
            validationSchema={validationSchema}
            onSubmit={(values) => {
              handleSubmit(values);
            }}
          >
            {({ values, errors, setFieldValue, handleSubmit }) => {
              return (
                <>
                  <span className="text-danger">{errors?.name && t(errors.name)}</span>
                  <Form onSubmit={handleSubmit}>
                    <div className="form-container">
                      {renderSteps(activeStep, values, setFieldValue, isUpdate, selectedProject)}
                    </div>
                    <div className="d-flex align-items-center justify-content-between pt-3">
                      {activeStep !== 0 && (
                        <Button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => setActiveStep((step) => step - 1)}
                        >
                          {t("button.back")}
                        </Button>
                      )}
                      <button type="submit" className="btn btn-outline-primary ms-auto">
                        {isSecondLastStep
                          ? isUpdate
                            ? isLoading
                              ? t("button.updating")
                              : t("button.update")
                            : isLoading
                            ? t("button.saving")
                            : t("button.save")
                          : t("button.next")}
                      </button>
                    </div>
                  </Form>
                </>
              );
            }}
          </Formik>
        ) : (
          <ProjectImage
            selectedProject={selectedProject}
            isUpdate={isUpdate}
            newId={newId}
            setOpenForm={setOpenForm}
            setActiveStep={setActiveStep}
          />
        )}
      </ModalBody>
    </Modal>
  );
}

export default ProjectForm;
